import * as z from "zod";

// Define the VehicleOrientation type to match the schema exactly
export type VehicleOrientation =
  | "Front"
  | "Rear"
  | "Left"
  | "Right"
  | "Top"
  | "Other"
  | `Other ${number}`;

// Point schema
export const MeasurementPointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

// Primary measurement schema
export const PrimaryMeasurementSchema = z.object({
  point_a: MeasurementPointSchema,
  point_b: MeasurementPointSchema,
  locked: z.boolean(),
  real_world_value: z.number().optional(),
});

// Secondary measurement schema - matches database exactly
export const SecondaryMeasurementSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Measurement name is required"),
  point_a: MeasurementPointSchema,
  point_b: MeasurementPointSchema,
  locked: z.boolean(),
});

// Marker note schema - matches database exactly
export const MarkerNoteSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  note: z
    .string()
    .min(1, "Note is required")
    .max(500, "Note must be under 500 characters"),
  created: z.string(), // ISO string timestamp
});

// Layer schema - matches database exactly
export const SnapLayerSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Layer name is required"),
  type: z.enum(["rectangle", "circle", "text", "custom", "line"]),
  x: z.number(),
  y: z.number(),
  width: z.number().min(0),
  height: z.number().min(0),
  rotation: z.number(),
  fill: z.string(),
  locked: z.boolean(),
  visible: z.boolean(),
  opacity: z.number().min(0).max(1).optional(),
  text: z.string().optional(),
  fontSize: z.number().positive().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.string().optional(),
  fontStyle: z.string().optional(),
  textCase: z
    .enum(["normal", "uppercase", "lowercase", "sentence", "title"])
    .optional(),
  customFile: z.string().optional(),
  strokeWidth: z.number().positive().optional(),
  flippedHorizontal: z.boolean().optional(),
  flippedVertical: z.boolean().optional(),
  zIndex: z.number(),
});

// Vehicle orientation validation - using custom validation to handle the union type
export const VehicleOrientationSchema = z.custom<VehicleOrientation>(
  (val) => {
    if (typeof val !== "string") return false;
    const validOrientations = [
      "Front",
      "Rear",
      "Left",
      "Right",
      "Top",
      "Other",
    ];
    if (validOrientations.includes(val)) return true;
    // Check if it matches "Other X" pattern where X is a number
    return /^Other \d+$/.test(val);
  },
  {
    message:
      "Orientation must be Front, Rear, Left, Right, Top, Other, or Other followed by a number (e.g., 'Other 1')",
  }
);

// Project schemas
export const UserSnapProjectSchema = z.object({
  id: z.string().uuid().optional(),
  user_id: z.string().uuid().optional(),
  company_id: z.string().uuid().optional().nullable(),
  vehicle_model_id: z.string().uuid().optional().nullable(),
  project_name: z.string().min(1, "Project name is required").max(255),
  is_shared: z.boolean().default(false),
  created: z.date().optional(),
  updated: z.date().optional(),
});

export const createUserSnapProjectSchema = UserSnapProjectSchema.omit({
  id: true,
  user_id: true,
  created: true,
  updated: true,
});

export const updateUserSnapProjectSchema =
  UserSnapProjectSchema.partial().extend({
    id: z.string().uuid(),
  });

// Snap schemas - streamlined to match database exactly
export const UserSnapSchema = z.object({
  id: z.string().uuid().optional(),
  project_id: z.string().uuid(),
  created_by: z.string().uuid().optional(),
  orientation: VehicleOrientationSchema,
  backdrop_url: z.string().nullable().optional(),
  backdrop_flipped_horizontal: z.boolean().optional().default(false),
  backdrop_flipped_vertical: z.boolean().optional().default(false),
  backdrop_rotation: z.number().min(0).max(360).optional().default(0),
  primary_measurement: PrimaryMeasurementSchema.nullable().optional(),
  measurements: z.array(SecondaryMeasurementSchema).default([]),
  layers: z.array(SnapLayerSchema).default([]),
  marker_notes: z.array(MarkerNoteSchema).default([]),
  notes: z.string().max(2000).optional().nullable(),
  created: z.date().optional(),
  updated: z.date().optional(),
});

// Create and update schemas
export const createUserSnapSchema = UserSnapSchema.omit({
  id: true,
  created_by: true,
  created: true,
  updated: true,
});

export const updateUserSnapSchema = UserSnapSchema.partial().extend({
  id: z.string().uuid(),
});

// Export inferred types for consistency
export type CreateUserSnapProjectInput = z.infer<
  typeof createUserSnapProjectSchema
>;
export type UpdateUserSnapProjectInput = z.infer<
  typeof updateUserSnapProjectSchema
>;
export type CreateUserSnapInput = z.infer<typeof createUserSnapSchema>;
export type UpdateUserSnapInput = z.infer<typeof updateUserSnapSchema>;

// Base component types
export type UserSnapProject = z.infer<typeof UserSnapProjectSchema>;
export type UserSnap = z.infer<typeof UserSnapSchema>;
export type SnapLayer = z.infer<typeof SnapLayerSchema>;
export type MeasurementPoint = z.infer<typeof MeasurementPointSchema>;
export type PrimaryMeasurement = z.infer<typeof PrimaryMeasurementSchema>;
export type SecondaryMeasurement = z.infer<typeof SecondaryMeasurementSchema>;
export type MarkerNote = z.infer<typeof MarkerNoteSchema>;
// VehicleOrientation type is defined at the top of the file

// Form-specific schema for creating projects
export const createSnapProjectFormSchema = z.object({
  project_name: z.string().min(1, "Project name is required").max(255),
  is_shared: z.boolean().default(false),
  company_id: z.string().optional(),
  vehicle_model_id: z.string().optional(),
  vehicle_model_name: z.string().optional(),
  vehicle_brand_id: z.string().optional(),
  vehicle_year: z.number().min(1900).max(2030).optional(),
  vehicle_type: z.string().optional(),
  orientation: VehicleOrientationSchema.default("Front"),
});

export type CreateSnapProjectFormInput = z.infer<
  typeof createSnapProjectFormSchema
>;
